/**
 * API client.
 *
 * # What this file deliberately does not contain
 *
 * Any token handling. No `Authorization` header, no token in memory, no token in storage. The
 * backend-for-frontend holds tokens server-side and the browser carries an opaque `httpOnly`
 * cookie it cannot read — see `docs/05-decisions/adr-0005-spa-auth-model.md`.
 *
 * The reasoning is specific rather than general best practice: an XSS anywhere in this
 * application's dependency tree must not yield a portable credential for a platform that brokers
 * access into hundreds of client networks. A gate check scans the built bundle to confirm no
 * token-shaped value appears in it.
 */

import type { CaptchaProvider } from '../auth/captcha';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * A challenge the server wants satisfied before it will look at this request.
     *
     * Present only on `code === 'challenge_required'`. Carried on the error rather than returned
     * normally because the request genuinely did not succeed — treating it as a success with a
     * "please retry" flag would put the retry decision in every call site instead of one.
     *
     * `kind` discriminates: proof of work is solved silently by `api.signIn`, while a CAPTCHA needs
     * a widget on screen and therefore has to reach the page.
     */
    readonly challenge?: ChallengeDemand,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The session is gone. The shell redirects to sign-in rather than retrying. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** What the server wants before it will evaluate a sign-in. */
export type ChallengeDemand =
  | { kind: 'proof_of_work'; token: string; difficulty: number }
  | { kind: 'captcha'; provider: CaptchaProvider; siteKey: string };

interface ErrorBody {
  code?: string;
  message?: string;
  kind?: string;
  challenge?: string;
  difficulty?: number;
  provider?: string;
  site_key?: string;
}

/** Reads a challenge demand out of an error body, if there is a complete one. */
function readChallenge(body: ErrorBody): ChallengeDemand | undefined {
  if (body.kind === 'proof_of_work' && body.challenge !== undefined && body.difficulty !== undefined) {
    return { kind: 'proof_of_work', token: body.challenge, difficulty: body.difficulty };
  }

  if (body.kind === 'captcha' && body.provider !== undefined && body.site_key !== undefined) {
    // Narrowed against the known set rather than cast. An unrecognized provider name would
    // otherwise reach `solveCaptcha` and try to load `undefined` as a script URL.
    const known = ['turnstile', 'hcaptcha', 'recaptcha'] as const;
    const provider = known.find((p) => p === body.provider);
    if (provider) {
      return { kind: 'captcha', provider, siteKey: body.site_key };
    }
  }

  return undefined;
}

/**
 * Issues a request against the API.
 *
 * `credentials: 'same-origin'` rather than `'include'`: the cookie is `__Host-` prefixed and
 * `SameSite=Strict`, so it is only ever sent to this origin anyway, and `'include'` would signal
 * an intent to send credentials cross-origin that we never want.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');

  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // The server also verifies the Origin header on mutations. This header is the double-submit
  // half of the pair; neither alone is sufficient, which is why both exist.
  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-Requested-With', 'conduit');
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: 'same-origin',
    redirect: 'error',
  });

  if (!response.ok) {
    let body: ErrorBody = {};
    try {
      body = (await response.json()) as ErrorBody;
    } catch {
      // A non-JSON error body — a proxy timeout page, for instance. Fall back to the status.
    }
    throw new ApiError(
      response.status,
      body.code ?? 'unknown',
      body.message ?? `request failed with status ${String(response.status)}`,
      readChallenge(body),
    );
  }

  if (response.status === 204) {
    // A 204 has no body. Callers that expect one type this as `undefined`.
    return undefined as T;
  }

  return (await response.json()) as T;
}

export interface HealthResponse {
  status: string;
  version: string;
}

export interface ReadinessResponse {
  status: string;
  database: string;
  cache: string;
}

export interface Identity {
  principal_id: string;
  tenant_id: string;
  roles: string[];
}

/** Alias used by the shell, which cares about roles rather than identity. */
export type WhoAmI = Identity;

export interface SignInResult extends Identity {
  must_change_password: boolean;
}

/** Where a ticket is in its lifecycle. */
export type TicketState =
  | 'new'
  | 'triage'
  | 'in_progress'
  | 'pending'
  | 'resolved'
  | 'closed'
  | 'canceled';

export interface Ticket {
  id: string;
  kind: string;
  state: TicketState;
  priority: string;
  domain: string | null;
  /** Attacker-controlled: anyone who can raise a ticket controls this. Render as text, never markup. */
  title: string;
  /** Null is unassigned. Surfaced rather than hidden — unowned work is what goes stale. */
  assignee_id: string | null;
  /** What was done. Present once the ticket has been resolved or canceled. */
  resolution: string | null;
  /**
   * The moves the server will accept from here, computed server-side from the same table it
   * enforces. Never recompute this in the SPA: a local copy drifts, and the screen then offers a
   * button that always fails.
   */
  allowed_next: TicketState[];
  created_at: string;
}

/** A ticket to raise. */
export interface NewTicket {
  kind: string;
  priority: string;
  title: string;
  description: string;
}

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export interface Snapshot {
  id: string;
  ci_id: string;
  ticket_id: string | null;
  bracket_role: string;
  source: string;
  captured_at: string;
  filename: string | null;
  byte_size: number;
  content_hash: string;
  parsed: boolean;
  parse_error: string | null;
  vendor: string | null;
  family: string | null;
  hostname: string | null;
  detection: { confidence: number | null; forced: boolean };
  summary: Record<string, number>;
}

export interface DiffResult {
  before: Snapshot;
  after: Snapshot;
  has_changes: boolean;
  summary: Record<string, number>;
  recommendations: { severity: string; text: string }[];
}

export interface TelemetryWindow {
  id: string;
  ci_id: string;
  ticket_id: string | null;
  bracket_role: string;
  source: string;
  observed_from: string;
  observed_to: string;
  total_lines: number;
  parsed_lines: number;
  parse_coverage: number;
  too_sparse_to_compare: boolean;
  is_syslog: boolean;
  summary: Record<string, unknown>;
}

export interface WindowComparison {
  before: TelemetryWindow;
  after: TelemetryWindow;
  contradicts_completion: boolean;
  findings: { severity: string; kind: string; text: string }[];
  deltas: Record<string, { before: number; after: number; change: number }>;
}

export interface Finding {
  id: string;
  ci_id: string | null;
  asset: string;
  source: string;
  cve: string | null;
  title: string;
  severity: string;
  known_exploited: boolean;
  risk_score: number | null;
  risk_explanation: string | null;
  state: string;
}

export interface LeaderboardEntry {
  cve: string;
  affected_findings: number;
  affected_assets: number;
  risk_removed_if_fixed: number;
  peak_risk: number;
  known_exploited: boolean;
}

export interface ShadowAsset {
  asset: string;
  open_findings: number;
}

export interface RiskConfigResponse {
  config: Record<string, unknown>;
  version: number;
}

export interface Collector {
  id: string;
  name: string;
  contract_key_fingerprint: string | null;
  last_seen_at: string | null;
  is_stale: boolean;
  agent_version: string | null;
  revoked: boolean;
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  body: JSON.stringify(body),
});

/** Where a campaign is in its life. */
export type CampaignState = 'planned' | 'scheduled' | 'in_progress' | 'completed' | 'canceled';

/** A unit of remediation work with a window and an owner group. */
export interface Campaign {
  id: string;
  name: string;
  /** Null means unassigned — nobody has picked the work up. */
  owner_group: string | null;
  state: CampaignState;
  window_start: string | null;
  window_end: string | null;
  /** What this campaign was expected to remove, recorded when it was planned. */
  projected_risk_reduction: number | null;
  /** What is still open across its findings. Falls as the work is done. */
  open_risk: number;
  finding_count: number;
}

/** A campaign to plan. */
export interface NewCampaign {
  name: string;
  owner_group?: string | null;
  window_start?: string | null;
  window_end?: string | null;
}

/**
 * `added` can be lower than `requested` — a finding already in the campaign, or one in a
 * compartment the caller cannot see, is skipped rather than erroring.
 */
export interface AddFindingsResult {
  requested: number;
  added: number;
}

/** One immutable record in the audit ledger. */
export interface AuditEntry {
  /** Position in the tenant's chain. Monotonic, so a gap is visible. */
  id: number;
  occurred_at: string;
  actor_id: string | null;
  event_type: string;
  subject_kind: string | null;
  subject_id: string | null;
  /** Identifiers and outcomes only — never payloads, never secrets. */
  detail: Record<string, unknown>;
  /** Hex SHA-256, published so the chain can be verified without trusting this service. */
  entry_hash: string;
}

/** The result of walking the chain. */
export interface AuditVerification {
  intact: boolean;
  /** What broke, in plain words. Null when intact. */
  broken: string | null;
  at_entry: number | null;
}

/** How one clock stands against its target. */
export interface ClockReading {
  /** `paused` is distinct from `ok`: a paused ticket is not counting down. */
  status: 'ok' | 'at_risk' | 'breached' | 'met' | 'missed_target' | 'paused';
  /** Working minutes, excluding every interval the ticket spent paused. */
  elapsed_minutes: number;
  target_minutes: number;
  /** Negative once breached, so a queue can sort by it directly. */
  remaining_minutes: number;
}

/** One ticket's standing against both promises. */
export interface SlaBoardRow {
  ticket_id: string;
  reference: string;
  priority: string;
  state: string;
  assignee_id: string | null;
  response: ClockReading;
  resolve: ClockReading;
}

/** What was promised, by priority, P1 first. */
export interface SlaPolicy {
  response_minutes: number[];
  resolve_minutes: number[];
  at_risk_percent: number;
  /** Always true today. Business-hours calendars are deliberately absent, not approximated. */
  round_the_clock: boolean;
}

/** A promise that was missed, recorded as it read at the moment it fired. */
export interface Escalation {
  ticket_id: string;
  reference: string;
  /** Attacker-controlled. Render as text, never as markup. */
  title: string;
  clock: string;
  level: string;
  raised_at: string;
  elapsed_minutes: number;
  target_minutes: number;
  /** Who owned it then. Null is itself the finding. */
  assignee_id: string | null;
  notified: boolean;
}

/** What a sweep did. */
export interface SweepResult {
  examined: number;
  raised: number;
  /** Already raised, so not raised again. Proves the ladder is idempotent. */
  already_known: number;
}

/** A system allowed to raise tickets in this tenant. */
export interface IngestionSource {
  id: string;
  name: string;
  kind: string;
  /** Enough to tell two keys apart. The key itself is never retrievable. */
  key_prefix: string;
  revoked: boolean;
  /** Null means it has never sent anything — worth chasing, not ignoring. */
  last_used_at: string | null;
  event_count: number;
}

/** One arrival, and what was decided about it. */
export interface IngestionEvent {
  received_at: string;
  source: string;
  external_id: string;
  /** Normalized. These are the values that actually decided correlation. */
  asset: string;
  signal: string;
  disposition: string;
  ticket_id: string | null;
  reference: string | null;
}

/** A declarative procedure. Files, loaded at startup — never editable at runtime. */
export interface Playbook {
  id: string;
  version: number;
  name: string;
  matches_kind: string | null;
  matches_domain: string | null;
  matches_signals: string[];
  /** `view_only`, `diagnose`, or `remediate`. A named profile, never a matrix of flags. */
  profile: string;
  recording: string;
  max_duration_minutes: number;
  /** A playbook may demand a second approver; it may never waive one. */
  requires_dual_approval: boolean;
  steps: string[];
}

/** What a playbook's request comes to against one CI's ceiling. */
export interface DerivedScope {
  ci_id: string;
  ci_name: string;
  targets: string[];
  /** Asked for, and refused by this CI's ceiling. Shown, not hidden. */
  dropped_targets: string[];
  usable: boolean;
}

/** The match for a ticket, and what it would yield. */
export interface PlaybookMatch {
  /** Null is a legitimate answer: the ticket is worked without a matched procedure. */
  playbook: Playbook | null;
  no_match_reason: string | null;
  derived: DerivedScope[];
}

/** A request for a scoped, time-boxed session against one ticket. */
export interface AccessRequest {
  id: string;
  ticket_id: string;
  reference: string;
  /** Attacker-controlled. Render as text, never as markup. */
  title: string;
  state: string;
  playbook_id: string | null;
  /** The playbook's request intersected with the CI's ceiling, frozen at request time. */
  derived_scope: Record<string, unknown>;
  requested_by: string;
  provider_approved_by: string | null;
  client_approved_by: string | null;
  /** True only for `approved`. "Nearly approved" is not approved. */
  permits_session: boolean;
  expires_at: string;
  decision_reason: string | null;
}

/**
 * A session the platform has issued.
 *
 * Note what is **not** here: an `endpoint_static_key`. The console learns the endpoint's identity
 * by verifying `contract` for itself, per ADR-0020 — a key served beside the contract would be a
 * key the platform chose, and the handshake would succeed against a platform-operated impostor
 * with nothing logging an error.
 */
export interface IssuedSession {
  remote_session_id: string;
  /** `wss://` URL of the relay. */
  relay_url: string;
  /**
   * 32 random bytes, hex. **Returned once**: the server keeps only its digest, so a screen that
   * loses this asks for a new session rather than asking for this one again.
   */
  rendezvous_token: string;
  /** When the TOKEN stops being usable — two minutes. Not when the session ends. */
  expires_at: string;
  contract: ServedContractEnvelope;
  /**
   * The platform-signed rendezvous ticket (ADR-0021).
   *
   * **Absent, not null**, when the rendezvous is still a bearer token — because this console sent
   * no key, or because the deployment signs no tickets. Presence is how the two modes are told
   * apart.
   */
  ticket?: IssuedTicket;
}

/** The ticket the console presents to the relay. */
export interface IssuedTicket {
  remote_session_id: string;
  /** Echoed back so the console can confirm the platform bound the key it sent. */
  console_public_key: string;
  expires_at: string;
  signature: string;
}

/** The client-signed contract, for the console to verify itself. */
export interface ServedContractEnvelope {
  /** The document as the client signed it. Passed to WASM unread; see `session/client.ts`. */
  document: Record<string, unknown>;
  signature_hex: string;
  verifying_key_hex: string;
  /** The same short form the key ceremony uses, so a person can compare it. */
  key_fingerprint: string;
}

/** The tenant's pinned contract verifying key. Fingerprint only — never the raw key. */
export interface ContractKey {
  pinned: boolean;
  fingerprint: string | null;
}

/** A stored Session Contract. The signature and canonical bytes are deliberately not returned. */
export interface SessionContractView {
  id: string;
  access_request_id: string;
  ticket_reference: string;
  key_fingerprint: string;
  not_before: string;
  expires_at: string;
  /** Unrevoked and inside its window, right now. */
  live: boolean;
  revoked_at: string | null;
  revocation_reason: string | null;
}

/** Operational health for this tenant. Every number answers a question about the loop. */
export interface OpsHealth {
  platform: { version: string; database: boolean; cache: boolean };
  loop_: {
    tickets_open: number;
    tickets_unassigned: number;
    tickets_escalated: number;
    awaiting_provider: number;
    awaiting_client: number;
    approved_unsigned: number;
    contracts_live: number;
    contracts_expiring: number;
    contracts_revoked_today: number;
  };
  estate: {
    cis_total: number;
    cis_reachable: number;
    cis_never_captured: number;
    collectors_total: number;
    collectors_healthy: number;
    collectors_stale: number;
    vulns_critical: number;
    vulns_high: number;
  };
  ledger: {
    events_today: number;
    events_total: number;
    last_event_at: string | null;
    contracts_refused_today: number;
  };
  as_of: string;
}

/** The deployment, as a platform administrator sees it. Counts and status — never contents. */
export interface PlatformView {
  totals: {
    tenants: number;
    active_tenants: number;
    principals: number;
    open_tickets: number;
    live_contracts: number;
    stale_collectors: number;
    unpinned_tenants: number;
    audit_events_today: number;
  };
  tenants: {
    id: string;
    slug: string;
    display_name: string;
    isolation_tier: string;
    status: string;
    /** Whether a key is pinned. Never the key, and never its fingerprint. */
    contract_key_pinned: boolean;
    principals: number;
    tickets: number;
    open_tickets: number;
    configuration_items: number;
    collectors: number;
    live_contracts: number;
    created_at: string;
    last_activity_at: string | null;
  }[];
}

/** A person or machine principal in the caller's own tenant. */
export interface PrincipalView {
  id: string;
  /** Attacker-influenced at creation. Render as text. */
  login: string;
  display_name: string;
  email: string | null;
  kind: string;
  status: string;
  roles: string[];
  must_change_password: boolean;
  locked_until: string | null;
  created_at: string;
}

/** Every call the SPA makes. Return types are explicit so a screen cannot silently receive `any`. */
/**
 * Signs in, solving a proof-of-work challenge if the server demands one.
 *
 * The retry happens here rather than in the page, so the page does not have to know the mechanism
 * exists. See `crates/conduit-control/src/challenge.rs` for why the server asks and what it is
 * actually buying.
 *
 * Exactly ONE retry. The server issues a fresh challenge with each demand, so a loop would solve
 * challenges indefinitely against an endpoint that has already decided to refuse — turning a
 * throttle into a way to make the browser burn its own CPU.
 */
async function signInWithChallenge(
  tenant: string,
  login: string,
  password: string,
  onChallenge?: (tried: number) => void,
  captchaToken?: string,
): Promise<SignInResult> {
  const attempt = (extra?: Record<string, unknown>): Promise<SignInResult> =>
    apiFetch<SignInResult>('/api/v1/auth/signin', json({ tenant, login, password, ...extra }));

  const first = captchaToken === undefined ? undefined : { captcha_token: captchaToken };

  try {
    return await attempt(first);
  } catch (err) {
    if (!(err instanceof ApiError) || err.code !== 'challenge_required' || !err.challenge) {
      throw err;
    }

    // A CAPTCHA needs a widget on screen, so it goes back to the page. Proof of work does not, and
    // is solved here so no call site has to know the mechanism exists.
    if (err.challenge.kind === 'captcha') throw err;

    const { solve } = await import('../auth/pow');
    const solution = await solve(err.challenge.token, err.challenge.difficulty, onChallenge);

    return await attempt({
      ...first,
      challenge: err.challenge.token,
      challenge_solution: solution,
    });
  }
}

export const api = {
  health: (): Promise<HealthResponse> => apiFetch<HealthResponse>('/api/health'),
  readiness: (): Promise<ReadinessResponse> => apiFetch<ReadinessResponse>('/api/ready'),
  whoami: (): Promise<Identity> => apiFetch<Identity>('/api/v1/me'),

  signIn: (
    tenant: string,
    login: string,
    password: string,
    onChallenge?: (tried: number) => void,
    captchaToken?: string,
  ): Promise<SignInResult> =>
    signInWithChallenge(tenant, login, password, onChallenge, captchaToken),
  signOut: (): Promise<undefined> => apiFetch<undefined>('/api/v1/auth/signout', { method: 'POST' }),

  tickets: (limit = 50): Promise<Page<Ticket>> =>
    apiFetch<Page<Ticket>>(`/api/v1/tickets?limit=${String(limit)}`),
  createTicket: (body: NewTicket): Promise<Ticket> =>
    apiFetch<Ticket>('/api/v1/tickets', json(body)),
  assignTicket: (id: string, assigneeId: string | null): Promise<undefined> =>
    apiFetch<undefined>(`/api/v1/tickets/${id}/assign`, json({ assignee_id: assigneeId })),
  moveTicket: (
    id: string,
    state: TicketState,
    resolution: string | null,
    assigneeId: string | null,
  ): Promise<undefined> =>
    apiFetch<undefined>(
      `/api/v1/tickets/${id}/state`,
      json({ state, resolution, assignee_id: assigneeId }),
    ),

  snapshots: (ciId: string, limit = 50): Promise<Snapshot[]> =>
    apiFetch<Snapshot[]>(`/api/v1/cis/${ciId}/config?limit=${String(limit)}`),
  diff: (beforeId: string, afterId: string): Promise<DiffResult> =>
    apiFetch<DiffResult>(`/api/v1/config/${beforeId}/diff?against=${afterId}`),

  telemetry: (ciId: string, limit = 50): Promise<TelemetryWindow[]> =>
    apiFetch<TelemetryWindow[]>(`/api/v1/cis/${ciId}/telemetry?limit=${String(limit)}`),
  compareWindows: (beforeId: string, afterId: string): Promise<WindowComparison> =>
    apiFetch<WindowComparison>(`/api/v1/telemetry/${beforeId}/compare?against=${afterId}`),

  vulnerabilities: (limit = 100): Promise<Finding[]> =>
    apiFetch<Finding[]>(`/api/v1/vulnerabilities?limit=${String(limit)}`),
  leaderboard: (limit = 20): Promise<LeaderboardEntry[]> =>
    apiFetch<LeaderboardEntry[]>(`/api/v1/vulnerabilities/leaderboard?limit=${String(limit)}`),
  shadowAssets: (limit = 100): Promise<ShadowAsset[]> =>
    apiFetch<ShadowAsset[]>(`/api/v1/vulnerabilities/shadow-assets?limit=${String(limit)}`),
  riskConfig: (): Promise<RiskConfigResponse> => apiFetch<RiskConfigResponse>('/api/v1/risk-config'),
  setRiskConfig: (config: Record<string, unknown>): Promise<RiskConfigResponse> =>
    apiFetch<RiskConfigResponse>('/api/v1/risk-config', {
      method: 'PUT',
      body: JSON.stringify(config),
    }),

  campaigns: (limit = 100): Promise<Campaign[]> =>
    apiFetch<Campaign[]>(`/api/v1/campaigns?limit=${String(limit)}`),
  createCampaign: (body: NewCampaign): Promise<{ id: string }> =>
    apiFetch<{ id: string }>('/api/v1/campaigns', json(body)),
  addToCampaign: (id: string, findingIds: string[]): Promise<AddFindingsResult> =>
    apiFetch<AddFindingsResult>(`/api/v1/campaigns/${id}/findings`, json({ finding_ids: findingIds })),
  setCampaignState: (id: string, state: CampaignState): Promise<undefined> =>
    apiFetch<undefined>(`/api/v1/campaigns/${id}/state`, json({ state })),

  accessRequests: (limit = 100): Promise<AccessRequest[]> =>
    apiFetch<AccessRequest[]>(`/api/v1/access-requests?limit=${String(limit)}`),
  requestAccess: (ticketId: string, ciId: string): Promise<{ id: string }> =>
    apiFetch<{ id: string }>('/api/v1/access-requests', json({ ticket_id: ticketId, ci_id: ciId })),
  approveAccess: (id: string, side: 'provider' | 'client'): Promise<undefined> =>
    apiFetch<undefined>(`/api/v1/access-requests/${id}/approve`, json({ side })),
  refuseAccess: (id: string, reason: string): Promise<undefined> =>
    apiFetch<undefined>(`/api/v1/access-requests/${id}/refuse`, json({ reason })),

  contractKey: (): Promise<ContractKey> => apiFetch<ContractKey>('/api/v1/contract-key'),
  pinContractKey: (
    publicKey: string,
    fingerprint: string,
    rotationReason: string | null,
  ): Promise<{ fingerprint: string; rotated: boolean }> =>
    apiFetch<{ fingerprint: string; rotated: boolean }>(
      '/api/v1/contract-key',
      json({ public_key: publicKey, fingerprint, rotation_reason: rotationReason }),
    ),
  /** The proposal the client signs. Returned as an object so the screen can offer it as a download. */
  contractProposal: (requestId: string): Promise<Record<string, unknown>> =>
    apiFetch<Record<string, unknown>>(`/api/v1/access-requests/${requestId}/proposal`),
  submitContract: (
    requestId: string,
    signed: Record<string, unknown>,
  ): Promise<{ contract_id: string; not_before: string; expires_at: string }> =>
    apiFetch<{ contract_id: string; not_before: string; expires_at: string }>(
      `/api/v1/access-requests/${requestId}/contract`,
      json(signed),
    ),
  /**
   * Opens a session against an approved, contract-backed request.
   *
   * The response carries the ONLY copy of the rendezvous token. Calling this twice issues two
   * sessions rather than returning the same one, which is why the screen calls it once on mount
   * and not on every render.
   */
  openSession: (requestId: string, consolePublicKey?: string): Promise<IssuedSession> =>
    apiFetch<IssuedSession>(
      `/api/v1/access-requests/${requestId}/session`,
      // The key is optional on the wire and optional here for the same reason: a deployment that
      // signs no tickets ignores it, and sending `{}` keeps the request shape stable either way.
      json(consolePublicKey ? { console_public_key: consolePublicKey } : {}),
    ),
  contracts: (limit = 100): Promise<SessionContractView[]> =>
    apiFetch<SessionContractView[]>(`/api/v1/contracts?limit=${String(limit)}`),
  revokeContract: (id: string, reason: string): Promise<undefined> =>
    apiFetch<undefined>(`/api/v1/contracts/${id}/revoke`, json({ reason })),

  opsHealth: (): Promise<OpsHealth> => apiFetch<OpsHealth>('/api/v1/ops/health'),

  platform: (): Promise<PlatformView> => apiFetch<PlatformView>('/api/v1/platform/tenants'),
  principals: (limit = 200): Promise<PrincipalView[]> =>
    apiFetch<PrincipalView[]>(`/api/v1/principals?limit=${String(limit)}`),
  setPrincipalRoles: (id: string, roles: string[]): Promise<undefined> =>
    apiFetch<undefined>(`/api/v1/principals/${id}/roles`, json({ roles })),
  setPrincipalStatus: (id: string, status: string): Promise<undefined> =>
    apiFetch<undefined>(`/api/v1/principals/${id}/status`, json({ status })),

  playbooks: (): Promise<Playbook[]> => apiFetch<Playbook[]>('/api/v1/playbooks'),
  playbookForTicket: (ticketId: string): Promise<PlaybookMatch> =>
    apiFetch<PlaybookMatch>(`/api/v1/tickets/${ticketId}/playbook`),

  ingestionSources: (): Promise<IngestionSource[]> =>
    apiFetch<IngestionSource[]>('/api/v1/ingestion-sources'),
  /** The response carries the ONLY copy of the key. Show it once; it cannot be fetched again. */
  createIngestionSource: (name: string, kind: string): Promise<{ id: string; key: string }> =>
    apiFetch<{ id: string; key: string }>('/api/v1/ingestion-sources', json({ name, kind })),
  revokeIngestionSource: (id: string): Promise<undefined> =>
    apiFetch<undefined>(`/api/v1/ingestion-sources/${id}/revoke`, { method: 'POST' }),
  ingestionEvents: (limit = 200): Promise<IngestionEvent[]> =>
    apiFetch<IngestionEvent[]>(`/api/v1/ingestion-events?limit=${String(limit)}`),

  slaPolicy: (): Promise<SlaPolicy> => apiFetch<SlaPolicy>('/api/v1/sla-policy'),
  slaBoard: (limit = 200): Promise<SlaBoardRow[]> =>
    apiFetch<SlaBoardRow[]>(`/api/v1/sla/board?limit=${String(limit)}`),
  escalations: (limit = 100): Promise<Escalation[]> =>
    apiFetch<Escalation[]>(`/api/v1/sla/escalations?limit=${String(limit)}`),
  runSlaSweep: (): Promise<SweepResult> =>
    apiFetch<SweepResult>('/api/v1/sla/sweep', { method: 'POST' }),

  audit: (limit = 200): Promise<AuditEntry[]> =>
    apiFetch<AuditEntry[]>(`/api/v1/audit?limit=${String(limit)}`),
  auditFor: (kind: string, id: string): Promise<AuditEntry[]> =>
    apiFetch<AuditEntry[]>(`/api/v1/audit/${kind}/${id}`),
  verifyAudit: (): Promise<AuditVerification> =>
    apiFetch<AuditVerification>('/api/v1/audit/verify'),

  collectors: (): Promise<Collector[]> => apiFetch<Collector[]>('/api/v1/collectors'),
};
